import { makeStopId } from "./defaults";
import { rgbaToHex } from "./rgba";
import type { Gradient, GradientStop } from "./types";

const LINEAR_PREFIX = "linear-gradient(";
const RADIAL_PREFIX = "radial-gradient(";
// Case-sensitive lowercase to match what serializeGradient emits. Capitalized
// or mixed-case gradient strings return null — we only parse our own canonical
// output (see section-02 interview: "Keep strict grammar").
const RADIAL_PREAMBLE_RE = /^\s*circle\s+at\s+center\s*$/;
const ANGLE_RE = /^\s*(-?\d+(?:\.\d+)?)\s*deg\s*$/;
const STOP_RE =
	/^\s*rgba\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)\s+(\d+(?:\.\d+)?)\s*%\s*$/;
// Matches a CSS variable stop: `var(--some-name) 50%`
const CSS_VAR_STOP_RE = /^\s*(var\(--[\w-]+\))\s+(\d+(?:\.\d+)?)\s*%\s*$/;

const clamp = (n: number, lo: number, hi: number): number =>
	Math.max(lo, Math.min(hi, n));

/**
 * Parse a CSS gradient string back to a Gradient object.
 *
 * Tolerant of whitespace but strict on overall shape (only accepts the exact
 * numeric/identifier forms that serializeGradient emits). Returns null on any
 * parse failure so callers can fall back to Solid mode.
 *
 * - Supported: linear-gradient(<deg>deg, <rgba-stop>+) and
 *   radial-gradient(circle at center, <rgba-stop>+).
 * - Unsupported (returns null): conic-gradient, elliptical radial, non-center
 *   radial, hex or named color stops, <2 stops, scientific/leading-dot numbers,
 *   capitalized identifiers.
 * - Stop IDs are freshly generated (not preserved across the string boundary).
 * - Alpha, RGB, and position are clamped to their valid ranges — note that
 *   clamped inputs re-serialize to a different canonical string than the input.
 * - Stops are returned in declared order (NOT re-sorted).
 */
export const parseGradient = (css: string): Gradient | null => {
	if (typeof css !== "string") return null;
	const trimmed = css.trim();
	if (trimmed.length === 0) return null;

	if (trimmed.startsWith(LINEAR_PREFIX)) {
		return parseLinear(trimmed);
	}
	if (trimmed.startsWith(RADIAL_PREFIX)) {
		return parseRadial(trimmed);
	}
	return null;
};

const extractBody = (input: string, prefix: string): string | null => {
	if (!input.endsWith(")")) return null;
	return input.slice(prefix.length, -1);
};

/**
 * Split body into top-level comma-separated segments, respecting paren depth.
 * Injection-safety: the splitter only tracks `(`/`)`/`,` — all other characters
 * are passed through verbatim. Each resulting segment is subsequently matched
 * against anchored `^…$` regexes (ANGLE_RE, STOP_RE, RADIAL_PREAMBLE_RE), so
 * arbitrary characters inside segments can only cause a mismatch, never escape
 * the grammar.
 * Returns null on unbalanced parens.
 */
const splitTopLevel = (body: string): string[] | null => {
	const segments: string[] = [];
	let depth = 0;
	let start = 0;
	for (let i = 0; i < body.length; i++) {
		const ch = body[i];
		if (ch === "(") depth++;
		else if (ch === ")") {
			depth--;
			if (depth < 0) return null;
		} else if (ch === "," && depth === 0) {
			segments.push(body.slice(start, i));
			start = i + 1;
		}
	}
	if (depth !== 0) return null;
	segments.push(body.slice(start));
	return segments;
};

const parseStop = (segment: string): GradientStop | null => {
	// Try CSS variable stop first: `var(--name) P%`
	const varMatch = segment.match(CSS_VAR_STOP_RE);
	if (varMatch) {
		const color = varMatch[1]; // e.g. "var(--theme-color-1)"
		const position = clamp(Number(varMatch[2]), 0, 100);
		return { id: makeStopId(), color, opacity: 1, position };
	}

	const m = segment.match(STOP_RE);
	if (!m) return null;
	// Number() of an anchored regex-matched numeric capture is always finite —
	// no explicit isFinite guard needed here.
	const r = clamp(Math.round(Number(m[1])), 0, 255);
	const g = clamp(Math.round(Number(m[2])), 0, 255);
	const b = clamp(Math.round(Number(m[3])), 0, 255);
	// Parser clamps opacity to [0,1] here; formatRgba also clamps on emit — this
	// double-guard is intentional defense-in-depth. Do not remove.
	const a = clamp(Number(m[4]), 0, 1);
	const position = clamp(Number(m[5]), 0, 100);
	// rgbaToHex with includeAlpha=false already returns uppercase 6-char "#RRGGBB",
	// so no additional normalization is needed. Invariant: color is always 7 chars.
	const color = rgbaToHex({ r, g, b, a: 1 }, false);
	return { id: makeStopId(), color, opacity: a, position };
};

const parseStops = (segments: string[]): GradientStop[] | null => {
	if (segments.length < 2) return null;
	const stops: GradientStop[] = [];
	for (const seg of segments) {
		const s = parseStop(seg);
		if (!s) return null;
		stops.push(s);
	}
	return stops;
};

const parseLinear = (input: string): Gradient | null => {
	const body = extractBody(input, LINEAR_PREFIX);
	if (body === null) return null;
	const segments = splitTopLevel(body);
	if (!segments || segments.length < 3) return null; // angle + ≥2 stops
	const angleMatch = segments[0].match(ANGLE_RE);
	if (!angleMatch) return null;
	const angle = Number(angleMatch[1]);
	if (!Number.isFinite(angle)) return null;
	const stops = parseStops(segments.slice(1));
	if (!stops) return null;
	return { type: "linear", angle, stops };
};

const parseRadial = (input: string): Gradient | null => {
	const body = extractBody(input, RADIAL_PREFIX);
	if (body === null) return null;
	const segments = splitTopLevel(body);
	if (!segments || segments.length < 3) return null; // preamble + ≥2 stops
	if (!RADIAL_PREAMBLE_RE.test(segments[0])) return null;
	const stops = parseStops(segments.slice(1));
	if (!stops) return null;
	return { type: "radial", stops };
};
